import { motion } from "framer-motion";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seo";
import { STUDIO_EMAIL, EMAIL_HREF } from "@/lib/links";

const ease = [0.22, 1, 0.36, 1];
const LAST_UPDATED = "20 February 2026";

const SECTIONS = [
    {
        no: "01",
        title: "About these terms",
        body: (
            <p>
                These terms govern any engagement with Digitalbeej Studio
                ("Digitalbeej", "we", "us"). By briefing us on a project,
                signing a proposal or paying an invoice, you agree to them.
                Anything client-specific — scope, timelines, deliverables — is
                captured in a separate proposal or Statement of Work (SOW) that
                takes precedence over anything general in this document.
            </p>
        ),
    },
    {
        no: "02",
        title: "Scope of work",
        body: (
            <p>
                Every engagement starts with a written proposal that describes
                the deliverables, timeline and pricing. Work outside that scope
                is treated as a change request and quoted separately. We
                won&apos;t begin work without written confirmation of the SOW.
            </p>
        ),
    },
    {
        no: "03",
        title: "Fees & payment",
        body: (
            <>
                <p>
                    Fees are quoted in Indian Rupees (INR) unless the SOW says
                    otherwise. Standard terms are 50% advance before project
                    commencement, with the remaining 50% payable before final
                    handover, deployment or transfer of the final
                    deliverables — unless the SOW specifies a different
                    milestone schedule.
                </p>
                <p className="mt-4">
                    Invoices are payable within 7 days of issue. Overdue
                    invoices may attract interest at 1.5% per month until
                    paid, and can result in paused work.
                </p>
            </>
        ),
    },
    {
        no: "04",
        title: "Revisions",
        body: (
            <p>
                Revision limits, if any, are not fixed by default — they are
                defined in the individual proposal, quotation or Statement of
                Work (SOW) for each project. Additional rounds beyond what is
                agreed are billed at the studio's standard hourly rate.
                We&apos;ll always flag before stepping outside included
                revisions.
            </p>
        ),
    },
    {
        no: "05",
        title: "Intellectual property",
        body: (
            <p>
                Upon receipt of full and final payment, ownership of the
                approved final deliverables transfers to the client, unless
                otherwise agreed in writing. Digitalbeej retains ownership of
                its proprietary methodologies, processes, templates,
                frameworks, reusable code, internal tools, AI workflows and
                pre-existing intellectual property used during the
                engagement.
            </p>
        ),
    },
    {
        no: "06",
        title: "Portfolio & marketing rights",
        body: (
            <p>
                Unless otherwise agreed in writing, Digitalbeej reserves the
                right to showcase completed work, project outcomes and
                non-confidential deliverables in its portfolio, website,
                social media, presentations, award submissions and other
                marketing materials. Confidential information, proprietary
                data and any work covered under a signed NDA will never be
                disclosed without the client&apos;s prior written consent.
            </p>
        ),
    },
    {
        no: "07",
        title: "Third-party tools & assets",
        body: (
            <p>
                Some engagements involve third-party platforms (analytics,
                advertising, e-commerce, CMS), fonts or stock media. Licences
                and platform fees are the client&apos;s responsibility unless
                the SOW says otherwise; we&apos;ll always flag them up front.
            </p>
        ),
    },
    {
        no: "08",
        title: "Confidentiality",
        body: (
            <p>
                We treat any non-public information you share as confidential
                and won&apos;t disclose it to third parties without your
                permission. If a formal NDA is preferred, we&apos;re happy to
                sign one before we start.
            </p>
        ),
    },
    {
        no: "09",
        title: "Warranties & liability",
        body: (
            <p>
                We do our best work — but no agency can guarantee specific
                marketing outcomes. To the extent permitted by law, our total
                liability for any engagement is limited to the fees paid to us
                in the preceding three months. We&apos;re not liable for
                indirect losses (missed revenue, downtime, reputational harm).
            </p>
        ),
    },
    {
        no: "10",
        title: "Termination",
        body: (
            <p>
                Either party may end an engagement with 30 days&apos; written
                notice. Fees for completed and in-progress work up to the
                notice date remain payable. Approved deliverables paid for in
                full remain with the client.
            </p>
        ),
    },
    {
        no: "11",
        title: "Governing law",
        body: (
            <p>
                These terms are governed by the laws of India. Any dispute is
                subject to the exclusive jurisdiction of the courts of Mumbai,
                Maharashtra.
            </p>
        ),
    },
    {
        no: "12",
        title: "Contact",
        body: (
            <p>
                Questions? Email us at{" "}
                <a
                    href={EMAIL_HREF}
                    className="underline underline-offset-4 decoration-olive hover:text-olive transition-colors duration-200"
                >
                    {STUDIO_EMAIL}
                </a>
                . For anything urgent, WhatsApp works too.
            </p>
        ),
    },
];

export default function Terms() {
    return (
        <>
            <Seo {...SEO.terms} />
            <PageHero
                data-testid="terms-page-hero"
                kicker="/ Terms · 07"
                meta={`Last updated · ${LAST_UPDATED}`}
                title="Terms"
                accent="of engagement"
                tagline="Straightforward rules for how we work together — nothing hidden, nothing tricky."
            />

            <section
                data-testid="terms-body"
                className="relative pb-24 lg:pb-32"
            >
                <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
                    <div className="grid grid-cols-12 gap-y-16 lg:gap-x-12">
                        <div className="col-span-12 lg:col-span-4 lg:sticky lg:top-32 lg:self-start">
                            <div className="text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                                On this page
                            </div>
                            <ol className="mt-6 space-y-3">
                                {SECTIONS.map((s) => (
                                    <li
                                        key={s.no}
                                        className="flex items-start gap-4 text-[15px]"
                                    >
                                        <span className="font-display font-black text-olive w-6 shrink-0">
                                            {s.no}
                                        </span>
                                        <a
                                            href={`#t-${s.no}`}
                                            className="text-black/70 hover:text-olive transition-colors duration-200 leading-snug"
                                        >
                                            {s.title}
                                        </a>
                                    </li>
                                ))}
                            </ol>
                        </div>

                        <div className="col-span-12 lg:col-span-8">
                            {SECTIONS.map((s, i) => (
                                <motion.article
                                    id={`t-${s.no}`}
                                    key={s.no}
                                    data-testid={`terms-section-${s.no}`}
                                    initial={{ opacity: 0, y: 24 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{
                                        duration: 0.7,
                                        ease,
                                        delay: (i % 3) * 0.05,
                                    }}
                                    className="pt-12 lg:pt-16 pb-10 border-b border-black/15 first:pt-0"
                                >
                                    <div className="flex items-baseline gap-4">
                                        <span className="font-display font-black text-3xl lg:text-4xl text-olive">
                                            {s.no}
                                        </span>
                                        <h2 className="font-display font-black uppercase tracking-tight text-2xl lg:text-3xl leading-tight">
                                            {s.title}
                                        </h2>
                                    </div>
                                    <div className="mt-6 text-black/75 leading-relaxed max-w-2xl text-[17px]">
                                        {s.body}
                                    </div>
                                </motion.article>
                            ))}

                            <div className="mt-12 text-xs uppercase tracking-[0.22em] font-bold text-black/55">
                                Last updated · {LAST_UPDATED}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
